import type { Buffer } from 'node:buffer';
import {
  type Account,
  AuthorityType,
  createAssociatedTokenAccountIdempotentInstruction,
  createAssociatedTokenAccountInstruction,
  createSetAuthorityInstruction,
  NATIVE_MINT,
  unpackAccount,
} from '@solana/spl-token';
import type { AccountInfo, TransactionInstruction } from '@solana/web3.js';
import { PublicKey } from '@solana/web3.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { decodeSponsorshipInstruction } from './layout.js';

export interface CanonicalAtaCreate {
  readonly payer: TransactionInstruction['keys'][number];
  readonly ata: TransactionInstruction['keys'][number];
  readonly owner: TransactionInstruction['keys'][number];
  readonly mint: TransactionInstruction['keys'][number];
  readonly systemProgram: TransactionInstruction['keys'][number];
  readonly tokenProgram: TransactionInstruction['keys'][number];
}

export type ResolveRelayAtaAccounts = (
  addresses: readonly PublicKey[],
) => Promise<readonly (AccountInfo<Buffer> | null)[]>;

export interface RelayAtaSponsorshipInput {
  readonly instructions: readonly TransactionInstruction[];
  readonly relayFeePayer: PublicKey;
  readonly wallet: PublicKey;
  readonly resolveAccounts: ResolveRelayAtaAccounts;
  /** Host's memory-only positive cache from a recent account snapshot. */
  readonly isKnownExistingAta?: ((ata: PublicKey) => boolean) | undefined;
}

export interface RelayAtaSponsorshipResult {
  readonly instructions: TransactionInstruction[];
  readonly sponsoredAtas: readonly PublicKey[];
  /** ATAs proved to exist by this invocation's authoritative RPC snapshot. */
  readonly confirmedExistingAtas: readonly PublicKey[];
}

export type RelayAtaSponsorshipFailureCode =
  | 'account_lookup_failed'
  | 'account_lookup_count_mismatch'
  | 'account_lookup_missing_result'
  | 'existing_ata_invalid';

export class RelayAtaSponsorshipError extends Error {
  readonly code: RelayAtaSponsorshipFailureCode;

  constructor(code: RelayAtaSponsorshipFailureCode, message: string) {
    super(message);
    this.name = 'RelayAtaSponsorshipError';
    this.code = code;
  }
}

/**
 * Returns the exact standard idempotent ATA create shape or null. The ATA
 * address is derived from owner, token program, and mint before sponsorship.
 */
export function decodeCanonicalIdempotentAtaCreate(
  instruction: TransactionInstruction,
): CanonicalAtaCreate | null {
  return decodeCanonicalAtaCreate(instruction, 'idempotent');
}

/**
 * Decodes the exclusive ATA Create emitted after a relay has proved its target
 * absent. Solana compiles signer and writable privileges globally, so owner
 * and mint privileges may be widened by unrelated instructions.
 */
export function decodeExclusiveAtaCreateFromCompiledMessage(
  instruction: TransactionInstruction,
  authenticatedWallet: PublicKey,
): CanonicalAtaCreate | null {
  const create = decodeAtaCreate(instruction, 'exclusive');
  if (
    create === null ||
    !create.payer.isSigner ||
    !create.payer.isWritable ||
    !create.ata.isWritable ||
    !create.owner.pubkey.equals(authenticatedWallet) ||
    !create.owner.isSigner
  ) {
    return null;
  }
  return create;
}

/**
 * Decodes the strict exclusive ATA Create emitted by the sponsorship transform.
 */
export function decodeCanonicalExclusiveAtaCreate(
  instruction: TransactionInstruction,
): CanonicalAtaCreate | null {
  return decodeCanonicalAtaCreate(instruction, 'exclusive');
}

type AtaCreateKind = 'exclusive' | 'idempotent';

function decodeCanonicalAtaCreate(
  instruction: TransactionInstruction,
  kind: AtaCreateKind,
): CanonicalAtaCreate | null {
  const create = decodeAtaCreate(instruction, kind);
  if (create === null) return null;
  const expected = createAtaInstruction(kind, create, create.payer.pubkey);
  return hasExactInstructionShape(instruction, expected) ? create : null;
}

function decodeAtaCreate(
  instruction: TransactionInstruction,
  kind: AtaCreateKind,
): CanonicalAtaCreate | null {
  if (!instruction.programId.equals(ASSOCIATED_TOKEN_PROGRAM_ID) || instruction.keys.length !== 6) {
    return null;
  }
  const [payer, ata, owner, mint, systemProgram, tokenProgram] = instruction.keys;
  if (
    payer === undefined ||
    ata === undefined ||
    owner === undefined ||
    mint === undefined ||
    systemProgram === undefined ||
    tokenProgram === undefined ||
    !systemProgram.pubkey.equals(SYSTEM_PROGRAM_ID) ||
    !tokenProgram.pubkey.equals(TOKEN_PROGRAM_ID) ||
    !ata.pubkey.equals(deriveAta(owner.pubkey, tokenProgram.pubkey, mint.pubkey))
  ) {
    return null;
  }
  const create = { payer, ata, owner, mint, systemProgram, tokenProgram };
  return bytesEqual(instruction.data, createAtaInstruction(kind, create, payer.pubkey).data)
    ? create
    : null;
}

/**
 * Finds wallet ATAs that may need relay-funded creation. This intentionally
 * excludes host-cached ATAs; callers use it to serialize only a first creation.
 */
export async function relayAtaSponsorshipCandidates(
  input: Pick<RelayAtaSponsorshipInput, 'instructions' | 'wallet' | 'isKnownExistingAta'>,
): Promise<readonly PublicKey[]> {
  return (await collectRelayAtaSponsorshipCandidates(input)).map((candidate) => candidate.ata);
}

async function collectRelayAtaSponsorshipCandidates(
  input: Pick<RelayAtaSponsorshipInput, 'instructions' | 'wallet' | 'isKnownExistingAta'>,
): Promise<RelayAtaCandidate[]> {
  const userFundedAtaTargets = await userFundedBuyQuoteAtaTargets(input.instructions, input.wallet);
  return collectCandidates(input.instructions, input.wallet, userFundedAtaTargets).filter(
    (candidate) => input.isKnownExistingAta?.(candidate.ata) !== true,
  );
}

/**
 * Replaces an absent, canonical wallet ATA's idempotent Create with exclusive
 * Create under the relay payer, then immediately transfers CloseAccount
 * authority. Callers that permit parallel submissions must serialize the first
 * such creation; an exclusive create deliberately fails if another creator won.
 */
export async function sponsorRelayAtaRent(
  input: RelayAtaSponsorshipInput,
): Promise<RelayAtaSponsorshipResult> {
  const candidates = await collectRelayAtaSponsorshipCandidates(input);
  if (candidates.length === 0) {
    return { instructions: [...input.instructions], sponsoredAtas: [], confirmedExistingAtas: [] };
  }

  // Sample account state once so simulation, final assembly, and retries share
  // the exact same build. A concurrent ATA creator can make this submission
  // fail; a fresh submission rechecks state and then leaves the create untouched.
  let accountInfos: readonly (AccountInfo<Buffer> | null)[];
  try {
    accountInfos = await input.resolveAccounts(candidates.map((candidate) => candidate.ata));
  } catch {
    throw new RelayAtaSponsorshipError('account_lookup_failed', 'Relay ATA account lookup failed');
  }
  if (accountInfos.length !== candidates.length) {
    throw new RelayAtaSponsorshipError(
      'account_lookup_count_mismatch',
      'Relay ATA account resolver returned an unexpected count',
    );
  }
  const sponsorableAtas = new Set<string>();
  const confirmedExistingAtas: PublicKey[] = [];
  for (const [index, candidate] of candidates.entries()) {
    const info = accountInfos[index];
    if (info === undefined) {
      throw new RelayAtaSponsorshipError(
        'account_lookup_missing_result',
        'Relay ATA account resolver omitted an account',
      );
    }
    if (info === null) {
      sponsorableAtas.add(candidate.ata.toBase58());
      continue;
    }
    assertExistingAtaMatches(candidate, info, input.wallet);
    confirmedExistingAtas.push(candidate.ata);
  }

  const sponsoredAtas: PublicKey[] = [];
  const instructions: TransactionInstruction[] = [];
  for (const instruction of input.instructions) {
    const create = decodeCanonicalIdempotentAtaCreate(instruction);
    if (create === null || !sponsorableAtas.has(create.ata.pubkey.toBase58())) {
      instructions.push(instruction);
      continue;
    }
    sponsorableAtas.delete(create.ata.pubkey.toBase58());
    sponsoredAtas.push(create.ata.pubkey);
    instructions.push(createAtaInstruction('exclusive', create, input.relayFeePayer));
    instructions.push(
      createSetAuthorityInstruction(
        create.ata.pubkey,
        input.wallet,
        AuthorityType.CloseAccount,
        input.relayFeePayer,
        [],
        create.tokenProgram.pubkey,
      ),
    );
  }
  return { instructions, sponsoredAtas, confirmedExistingAtas };
}

interface RelayAtaCandidate {
  readonly ata: PublicKey;
  readonly mint: PublicKey;
  readonly tokenProgram: PublicKey;
}

function collectCandidates(
  instructions: readonly TransactionInstruction[],
  wallet: PublicKey,
  userFundedAtaTargets: ReadonlySet<string>,
): RelayAtaCandidate[] {
  const seen = new Set<string>();
  const candidates: RelayAtaCandidate[] = [];
  for (const instruction of instructions) {
    const create = decodeCanonicalIdempotentAtaCreate(instruction);
    if (
      create === null ||
      !create.payer.pubkey.equals(wallet) ||
      !create.owner.pubkey.equals(wallet) ||
      isNativeMint(create.mint.pubkey) ||
      userFundedAtaTargets.has(create.ata.pubkey.toBase58()) ||
      seen.has(create.ata.pubkey.toBase58())
    ) {
      continue;
    }
    seen.add(create.ata.pubkey.toBase58());
    candidates.push({
      ata: create.ata.pubkey,
      mint: create.mint.pubkey,
      tokenProgram: create.tokenProgram.pubkey,
    });
  }
  return candidates;
}

async function userFundedBuyQuoteAtaTargets(
  instructions: readonly TransactionInstruction[],
  wallet: PublicKey,
): Promise<ReadonlySet<string>> {
  const targets = new Set<string>();
  const priorSellTargets = new Set<string>();
  for (const instruction of instructions) {
    const decoded = await decodeSponsorshipInstruction(instruction);
    if (decoded === null) continue;
    const trader = decoded.account('trader');
    const quoteAta = decoded.account('trader_quote_ata');
    if (!trader?.pubkey.equals(wallet) || quoteAta === null) continue;
    if (decoded.layout.name === 'sell') {
      priorSellTargets.add(quoteAta.pubkey.toBase58());
      continue;
    }
    if (decoded.layout.name === 'buy' && !priorSellTargets.has(quoteAta.pubkey.toBase58())) {
      targets.add(quoteAta.pubkey.toBase58());
    }
  }
  return targets;
}

function assertExistingAtaMatches(
  candidate: RelayAtaCandidate,
  info: AccountInfo<Buffer>,
  wallet: PublicKey,
): void {
  let account: Account;
  try {
    account = unpackAccount(candidate.ata, info, candidate.tokenProgram);
  } catch {
    throw new RelayAtaSponsorshipError(
      'existing_ata_invalid',
      'Existing relay ATA cannot be validated',
    );
  }
  if (
    !account.isInitialized ||
    !account.mint.equals(candidate.mint) ||
    !account.owner.equals(wallet)
  ) {
    throw new RelayAtaSponsorshipError(
      'existing_ata_invalid',
      'Existing relay ATA does not match the requested wallet and mint',
    );
  }
}

function createAtaInstruction(
  kind: AtaCreateKind,
  create: CanonicalAtaCreate,
  payer: PublicKey,
): TransactionInstruction {
  const build =
    kind === 'exclusive'
      ? createAssociatedTokenAccountInstruction
      : createAssociatedTokenAccountIdempotentInstruction;
  return build(
    payer,
    create.ata.pubkey,
    create.owner.pubkey,
    create.mint.pubkey,
    create.tokenProgram.pubkey,
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );
}

function hasExactInstructionShape(
  instruction: TransactionInstruction,
  expected: TransactionInstruction,
): boolean {
  return (
    instruction.programId.equals(expected.programId) &&
    bytesEqual(instruction.data, expected.data) &&
    instruction.keys.length === expected.keys.length &&
    instruction.keys.every((key, index) => {
      const expectedKey = expected.keys[index];
      return (
        expectedKey !== undefined &&
        key.pubkey.equals(expectedKey.pubkey) &&
        key.isSigner === expectedKey.isSigner &&
        key.isWritable === expectedKey.isWritable
      );
    })
  );
}

function deriveAta(owner: PublicKey, tokenProgram: PublicKey, mint: PublicKey): PublicKey {
  const [ata] = PublicKey.findProgramAddressSync(
    [owner.toBuffer(), tokenProgram.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );
  return ata;
}

function isNativeMint(mint: PublicKey): boolean {
  return mint.equals(NATIVE_MINT);
}

function bytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  return left.length === right.length && left.every((byte, index) => byte === right[index]);
}
